import { useEffect, useMemo, useCallback } from "react";
import { autofillAllocations, allocationRemainderCents, type AllocationRow, type RemittanceAllocationConfig } from "@capture/shared";
import { useStationStore } from "../state/station-store";
import { putAllocations } from "../api/client";
import type { StationTransaction } from "../api/client";

/** Draft allocation rows live in the station store (per txn). Seed from the
 *  persisted rows, else autofill by the prescribed rule. Remainder is derived. */
export function useAllocationDraft(transaction: StationTransaction, config: RemittanceAllocationConfig, paymentTotalCents: number) {
  const draft = useStationStore((s) => s.allocationDraft[transaction.id]);
  const setAllocationDraft = useStationStore((s) => s.setAllocationDraft);

  // Seed once (persisted rows win; else autofill).
  useEffect(() => {
    if (draft) return;
    const seeded = transaction.allocations ?? autofillAllocations(paymentTotalCents, config);
    setAllocationDraft(transaction.id, seeded);
    // Seeding is intentional-once per transaction id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction.id]);

  const rows: AllocationRow[] = draft ?? [];
  const remainder = useMemo(() => allocationRemainderCents(paymentTotalCents, rows), [paymentTotalCents, rows]);

  const setRow = useCallback((targetId: string, amountCents: number) => {
    const next = (draft ?? []).map((r) => (r.targetId === targetId ? { ...r, amountCents, source: "operator" as const } : r));
    setAllocationDraft(transaction.id, next);
  }, [draft, setAllocationDraft, transaction.id]);

  const autofill = useCallback(() => setAllocationDraft(transaction.id, autofillAllocations(paymentTotalCents, config)), [config, paymentTotalCents, setAllocationDraft, transaction.id]);
  const save = useCallback(() => putAllocations(transaction.id, draft ?? []), [draft, transaction.id]);

  return { rows, remainder, setRow, autofill, save };
}
